import apiClient from './apiClient';

// ─── Types ───

export interface PagePermissionDef {
  pageKey: string;
  displayName: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface MyPermissionsResponse {
  pages: Record<string, boolean>;
  isOverridden: boolean;
}

export interface UserPermissionsResponse {
  userId: number;
  pageKeys: string[];
  hasOverride: boolean;
}

export interface PermissionGroup {
  id: number;
  name: string;
  contextRestricted: boolean;
}

export interface GroupPermissionAction {
  actionId: number;
  areaName: string;
  actionName: string;
  isGranted: boolean;
}

// ─── Helper ───

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap<T>(responseData: any): T {
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return responseData.data;
  }
  return responseData;
}

// ─── API ───

export const pagePermissionApi = {
  getPageDefinitions: async (): Promise<PagePermissionDef[]> => {
    const response = await apiClient.get('/page-permissions');
    return unwrap<PagePermissionDef[]>(response.data);
  },

  getMyPermissions: async (): Promise<MyPermissionsResponse> => {
    const response = await apiClient.get('/page-permissions/me');
    return unwrap<MyPermissionsResponse>(response.data);
  },

  getUserPermissions: async (userId: number): Promise<UserPermissionsResponse> => {
    const response = await apiClient.get(`/page-permissions/users/${userId}`);
    return unwrap<UserPermissionsResponse>(response.data);
  },

  setUserPermissions: async (userId: number, pageKeys: string[]): Promise<void> => {
    await apiClient.put(`/page-permissions/users/${userId}`, { pageKeys });
  },

  resetUserPermissions: async (userId: number): Promise<void> => {
    await apiClient.delete(`/page-permissions/users/${userId}`);
  },

  // ─── Group Permission Management ───

  getGroups: async (): Promise<PermissionGroup[]> => {
    const response = await apiClient.get('/page-permissions/groups');
    return unwrap<PermissionGroup[]>(response.data);
  },

  getGroupPermissions: async (groupId: number): Promise<GroupPermissionAction[]> => {
    const response = await apiClient.get(`/page-permissions/groups/${groupId}`);
    return unwrap<GroupPermissionAction[]>(response.data);
  },

  setGroupPermissions: async (groupId: number, actionIds: number[]): Promise<void> => {
    await apiClient.put(`/page-permissions/groups/${groupId}`, { actionIds });
  },

  getUserGroups: async (userId: number): Promise<number[]> => {
    const response = await apiClient.get(`/page-permissions/users/${userId}/groups`);
    return unwrap<number[]>(response.data);
  },

  setUserGroups: async (userId: number, groupIds: number[]): Promise<void> => {
    await apiClient.put(`/page-permissions/users/${userId}/groups`, { groupIds });
  },
};